import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  collection,
  getDocs,
  addDoc,
  getDoc,
  serverTimestamp,
  Timestamp,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import {
  notify,
  scheduleAppointmentStartNotification,
} from "../utils/notifications";
import { decryptPatient } from "../utils/encryption";

// Helper function to parse 12-hour format time string to 24-hour format
function parse12HourTime(timeStr) {
  if (!timeStr) return null;
  // Format: "HH:MM AM/PM" or "H:MM AM/PM"
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) {
    // If it's already in 24-hour format (HH:MM), return as is
    const parts = timeStr.split(":");
    if (parts.length === 2) {
      return { hours: parseInt(parts[0], 10), minutes: parseInt(parts[1], 10) };
    }
    return null;
  }
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();

  if (period === "PM" && hours !== 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }

  return { hours, minutes };
}

function AddAppointmentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEditMode = !!editId;

  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [type, setType] = useState("Consultation");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(isEditMode);
  const [patientsLoaded, setPatientsLoaded] = useState(false);

  // Multiple appointments state
  const [isMultiple, setIsMultiple] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [frequency, setFrequency] = useState("daily"); // daily, weekly, selectedDays
  const [selectedDays, setSelectedDays] = useState([]); // Array of day numbers (0=Sunday, 6=Saturday)

  // Get today's date in YYYY-MM-DD format for min date
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        // Load patients
        const snap = await getDocs(collection(db, "patients"));
        const data = snap.docs
          .map((d) => decryptPatient({ id: d.id, ...d.data() }))
          .filter((p) => p.userId === currentUser.uid);
        setPatients(data);
        setPatientsLoaded(true);

        // If edit mode, load appointment data
        if (isEditMode && editId) {
          const apptDoc = await getDoc(doc(db, "appointments", editId));
          if (apptDoc.exists()) {
            const apptData = apptDoc.data();
            setPatientId(apptData.patientId || "");
            setType(apptData.reason || "Consultation");
            setNotes(apptData.notes || "");

            // Set date and time from appointment
            if (apptData.start) {
              const startDate = apptData.start?.toDate
                ? apptData.start.toDate()
                : new Date(apptData.start);
              const dateStr = startDate.toISOString().split("T")[0];
              const timeStr = startDate.toTimeString().slice(0, 5);
              setDate(dateStr);
              setTime(timeStr);

              // Set end time if available (in 12-hour format)
              if (apptData.end) {
                const endDate = apptData.end?.toDate
                  ? apptData.end.toDate()
                  : new Date(apptData.end);
                const endTimeStr = endDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                });
                setEndTime(endTimeStr);
              } else {
                // Default to 30 minutes after start (in 12-hour format)
                const defaultEnd = new Date(startDate.getTime() + 30 * 60000);
                const endTimeStr = defaultEnd.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                });
                setEndTime(endTimeStr);
              }
            }

            // Handle reschedule reason
            const rescheduleReason = searchParams.get("rescheduleReason");
            if (rescheduleReason) {
              const decodedReason = decodeURIComponent(rescheduleReason);
              setNotes((prev) =>
                prev
                  ? `${prev}\n\nReschedule reason: ${decodedReason}`
                  : `Reschedule reason: ${decodedReason}`
              );
            }
          }
        } else {
          // Pre-fill from URL params if provided
          const urlPatientId = searchParams.get("patientId");
          const urlDate = searchParams.get("date");
          const urlTime = searchParams.get("time");
          const urlType = searchParams.get("type");
          const urlNotes = searchParams.get("notes");

          if (urlPatientId) setPatientId(urlPatientId);
          if (urlDate) setDate(urlDate);
          if (urlTime) {
            setTime(urlTime);
            // Auto-set end time to 30 minutes after start (in 12-hour format)
            const [hh, mm] = urlTime.split(":").map((v) => parseInt(v));
            const startDate = new Date();
            startDate.setHours(hh, mm, 0, 0);
            const endDate = new Date(startDate.getTime() + 30 * 60000);
            const endTimeStr = endDate.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
            setEndTime(endTimeStr);
          }
          if (urlType) setType(urlType);
          if (urlNotes) setNotes(decodeURIComponent(urlNotes));
        }
      } catch (e) {
        console.warn("Failed to load data", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isEditMode, editId, searchParams]);

  // Generate end time options in 30-minute intervals after start time (12-hour format)
  const generateEndTimeOptions = (startTime) => {
    if (!startTime) return [];
    const options = [];
    const [hh, mm] = startTime.split(":").map((v) => parseInt(v));
    const startMinutes = hh * 60 + mm;

    // Generate options from 30 minutes to 3 hours after start time
    for (let i = 30; i <= 180; i += 30) {
      const totalMinutes = startMinutes + i;
      const hours = Math.floor(totalMinutes / 60) % 24;
      const minutes = totalMinutes % 60;

      // Convert to 12-hour format
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      const timeStr = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      options.push(timeStr);
    }
    return options;
  };

  // Generate appointment dates based on start date, end date, and frequency
  const generateAppointmentDates = (start, end, freq, timeStr, days = []) => {
    const dates = [];
    const [hh, min] = String(timeStr)
      .split(":")
      .map((v) => parseInt(v, 10));
    const startDate = new Date(start);
    const endDate = new Date(end);
    endDate.setHours(23, 59, 59, 999); // Include the end date

    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      let shouldInclude = false;
      const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

      if (freq === "daily") {
        shouldInclude = true;
      } else if (freq === "weekly") {
        // Only include if it's the same day of week as start date
        shouldInclude = dayOfWeek === startDate.getDay();
      } else if (freq === "daily-monday-friday") {
        // Monday (1) to Friday (5)
        shouldInclude = dayOfWeek >= 1 && dayOfWeek <= 5;
      } else if (freq === "daily-monday-saturday") {
        // Monday (1) to Saturday (6)
        shouldInclude = dayOfWeek >= 1 && dayOfWeek <= 6;
      } else if (freq === "selectedDays") {
        // Only include if current day is in selected days array
        shouldInclude = days.includes(dayOfWeek);
      }

      if (shouldInclude) {
        const appointmentDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate(),
          hh || 0,
          min || 0,
          0,
          0
        );
        dates.push(appointmentDate);
      }

      // Move to next date
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (isMultiple && !isEditMode) {
      // Multiple appointments mode
      if (!patientId || !startDate || !endDate || !time) {
        setError("Please fill all required fields for multiple appointments");
        return;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        setError("End date must be after start date");
        return;
      }

      const now = new Date();
      if (start < now) {
        setError("Start date cannot be in the past");
        return;
      }

      if (frequency === "selectedDays" && selectedDays.length === 0) {
        setError("Please select at least one day of the week");
        return;
      }

      const appointmentDates = generateAppointmentDates(
        start,
        end,
        frequency,
        time,
        selectedDays
      );

      if (appointmentDates.length === 0) {
        setError(
          "No valid appointments generated. Please check your date range."
        );
        return;
      }

      if (appointmentDates.length > 50) {
        setError(
          "Cannot create more than 50 appointments at once. Please reduce the date range."
        );
        return;
      }

      setSaving(true);
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("You must be logged in");
          setSaving(false);
          return;
        }

        const selectedPatient = patients.find((p) => p.id === patientId);
        const batch = writeBatch(db);
        const appointmentsRef = collection(db, "appointments");

        // Create appointments in batches (Firestore batch limit is 500)
        appointmentDates.forEach((apptDate) => {
          const apptRef = doc(appointmentsRef);
          batch.set(apptRef, {
            patientName: selectedPatient?.name || "Unknown",
            patientId,
            reason: type,
            start: Timestamp.fromDate(apptDate),
            notes,
            status: "Confirmed",
            avatarUrl: selectedPatient?.avatarUrl || "",
            userId: currentUser.uid,
            createdAt: serverTimestamp(),
          });
        });

        await batch.commit();

        // Update patient status to "Active" if currently "New"
        if (selectedPatient && selectedPatient.status === "New") {
          const patientDocRef = doc(db, "patients", patientId);
          await updateDoc(patientDocRef, {
            status: "Active",
          });
        }

        navigate("/appointments");
      } catch (e) {
        console.error(e);
        setError("Failed to create appointments");
      } finally {
        setSaving(false);
      }
    } else {
      // Single appointment mode (existing logic)
      if (!patientId || !date || !time) {
        setError("Please fill all required fields");
        return;
      }

      setSaving(true);
      try {
        const selectedPatient = patients.find((p) => p.id === patientId);
        // Robust local datetime construction to avoid timezone/string parsing issues
        const [yy, mm, dd] = String(date)
          .split("-")
          .map((v) => parseInt(v, 10));
        const [hh, min] = String(time)
          .split(":")
          .map((v) => parseInt(v, 10));
        const datetime = new Date(
          yy,
          (mm || 1) - 1,
          dd || 1,
          hh || 0,
          min || 0,
          0,
          0
        );

        // Calculate end datetime if endTime is provided (parse from 12-hour format)
        let endDatetime = null;
        if (endTime) {
          const parsedEnd = parse12HourTime(endTime);
          if (parsedEnd) {
            endDatetime = new Date(
              yy,
              (mm || 1) - 1,
              dd || 1,
              parsedEnd.hours || 0,
              parsedEnd.minutes || 0,
              0,
              0
            );
          }
        }
        if (isNaN(datetime.getTime())) {
          setError("Invalid date/time selection");
          setSaving(false);
          return;
        }

        // Prevent appointments in the past (unless editing and keeping the same date/time)
        if (!isEditMode) {
          const now = new Date();
          if (datetime < now) {
            setError(
              "Cannot schedule appointments in the past. Please select a future date and time."
            );
            setSaving(false);
            return;
          }
        } else {
          // When editing, allow same date/time but not past dates
          const now = new Date();
          // Allow 5 minute buffer for same time
          const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
          if (datetime < fiveMinutesAgo) {
            setError(
              "Cannot reschedule appointments to the past. Please select today or a future date and time."
            );
            setSaving(false);
            return;
          }
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("You must be logged in");
          setSaving(false);
          return;
        }

        if (isEditMode && editId) {
          // Update existing appointment (reschedule)
          const rescheduleReason = searchParams.get("rescheduleReason");
          const updateData = {
            patientName: selectedPatient?.name || "Unknown",
            patientId,
            reason: type,
            start: Timestamp.fromDate(datetime),
            notes,
            avatarUrl: selectedPatient?.avatarUrl || "",
            rescheduleReason: rescheduleReason
              ? decodeURIComponent(rescheduleReason)
              : null,
            rescheduledAt: new Date(),
            status: "Confirmed", // Reset status when rescheduling
          };
          if (endDatetime) {
            updateData.end = Timestamp.fromDate(endDatetime);
          }
          await updateDoc(doc(db, "appointments", editId), updateData);

          // Send notification for reschedule
          if (currentUser) {
            await notify(currentUser.uid, {
              type: "appointment_rescheduled",
              appointmentId: editId,
              patientName: selectedPatient?.name || "Patient",
              title: "Appointment Rescheduled",
              message: `Appointment with ${
                selectedPatient?.name || "patient"
              } has been rescheduled.${
                rescheduleReason
                  ? ` Reason: ${decodeURIComponent(rescheduleReason)}`
                  : ""
              }`,
            });
          }
        } else {
          // Create new appointment
          const appointmentData = {
            patientName: selectedPatient?.name || "Unknown",
            patientId,
            reason: type,
            start: Timestamp.fromDate(datetime),
            notes,
            status: "Confirmed",
            avatarUrl: selectedPatient?.avatarUrl || "",
            userId: currentUser.uid,
            createdAt: serverTimestamp(),
          };
          if (endDatetime) {
            appointmentData.end = Timestamp.fromDate(endDatetime);
          }
          const newAppointmentRef = await addDoc(
            collection(db, "appointments"),
            appointmentData
          );

          // Schedule notification for appointment start based on user's notification frequency setting
          if (currentUser) {
            await scheduleAppointmentStartNotification(currentUser.uid, {
              id: newAppointmentRef.id,
              ...appointmentData,
            });
          }

          // Update patient status based on appointment creation
          if (selectedPatient) {
            const patientDocRef = doc(db, "patients", patientId);
            // Always set status to "Appointment Schedule" when creating an appointment
            await updateDoc(patientDocRef, {
              status: "Appointment Schedule",
            });
          }
        }

        navigate("/appointments");
      } catch (e) {
        console.error(e);
        setError("Failed to create appointment");
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <div className="screen">
      <header className="form-header">
        <button className="icon-btn" onClick={() => navigate("/appointments")}>
          ←
        </button>
        <h2>{isEditMode ? "Edit Appointment" : "Add Appointment"}</h2>
        <button
          type="submit"
          form="appointment-form"
          className="save-btn"
          disabled={
            saving ||
            loading ||
            (patientsLoaded && patients.length === 0 && !isEditMode)
          }
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </header>

      {loading ? (
        <div className="form-card">
          <p className="muted">Loading appointment details...</p>
        </div>
      ) : patientsLoaded && patients.length === 0 && !isEditMode ? (
        <div className="form-card">
          <div style={{ textAlign: "center", padding: "48px 24px" }}>
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: "64px",
                color: "var(--text-muted)",
                marginBottom: "16px",
                display: "block",
              }}
            >
              person_add
            </span>
            <h3
              style={{
                margin: "0 0 8px",
                color: "var(--text-primary)",
                fontSize: "20px",
                fontWeight: "800",
              }}
            >
              No Patients Found
            </h3>
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "15px",
                fontWeight: "500",
                margin: "0 0 24px",
                lineHeight: "1.6",
              }}
            >
              You need to add at least one patient before you can schedule an
              appointment.
            </p>
            <button
              type="button"
              onClick={() => navigate("/patients/new")}
              style={{
                background: "var(--register-primary)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background =
                  "var(--register-primary-hover)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "var(--register-primary)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "20px" }}
              >
                person_add
              </span>
              Add Patient
            </button>
          </div>
        </div>
      ) : (
        <form
          id="appointment-form"
          className="form-card"
          onSubmit={handleSubmit}
        >
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <label className="form-label">Patient Details</label>
            <select
              className="form-select"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              required
              disabled={patients.length === 0}
            >
              <option value="">Select Patient</option>
              {patients.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {patients.length === 0 && (
              <p
                style={{
                  marginTop: "8px",
                  fontSize: "13px",
                  color: "var(--text-muted)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "16px" }}
                >
                  info
                </span>
                No patients available. Please add a patient first.
              </p>
            )}
          </div>

          <div className="form-section">
            <label className="form-label">Appointment Type</label>
            <div className="type-buttons">
              {["Consultation", "Therapy", "Follow-up"].map((t) => (
                <button
                  key={t}
                  type="button"
                  className={type === t ? "type-btn active" : "type-btn"}
                  onClick={() => setType(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {!isEditMode && (
            <div className="form-section">
              <label
                className="form-label"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={isMultiple}
                  onChange={(e) => {
                    setIsMultiple(e.target.checked);
                    if (!e.target.checked) {
                      setStartDate("");
                      setEndDate("");
                      setFrequency("daily");
                      setSelectedDays([]);
                    }
                  }}
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                />
                <span>Schedule multiple appointments</span>
              </label>
            </div>
          )}

          {isMultiple && !isEditMode ? (
            <>
              <div className="form-section">
                <label className="form-label">Start Date & Time</label>
                <div className="datetime-row">
                  <input
                    type="date"
                    className="form-input"
                    value={startDate}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      const today = getTodayDate();
                      if (selectedDate < today) {
                        setError(
                          "Cannot select past dates. Please choose today or a future date."
                        );
                        return;
                      }
                      setError("");
                      setStartDate(selectedDate);
                      if (!endDate || selectedDate > endDate) {
                        setEndDate(selectedDate);
                      }
                    }}
                    min={getTodayDate()}
                    required
                  />
                  <input
                    type="time"
                    className="form-input"
                    value={time}
                    onChange={(e) => {
                      setTime(e.target.value);
                      setError("");
                    }}
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={endDate}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    if (startDate && selectedDate < startDate) {
                      setError("End date must be after start date");
                      return;
                    }
                    setError("");
                    setEndDate(selectedDate);
                  }}
                  min={startDate || getTodayDate()}
                  required
                />
              </div>

              <div className="form-section">
                <label className="form-label">Frequency</label>
                <select
                  className="form-select"
                  value={frequency}
                  onChange={(e) => {
                    setFrequency(e.target.value);
                    if (e.target.value !== "selectedDays") {
                      setSelectedDays([]);
                    }
                  }}
                >
                  <option value="daily">Daily</option>
                  <option value="daily-monday-friday">
                    Daily - Monday to Friday
                  </option>
                  <option value="daily-monday-saturday">
                    Daily - Monday to Saturday
                  </option>
                  <option value="weekly">Weekly</option>
                  <option value="selectedDays">Selected Days</option>
                </select>
              </div>

              {frequency === "selectedDays" && (
                <div className="form-section">
                  <label className="form-label">Select Days</label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      gap: "8px",
                    }}
                  >
                    {[
                      { day: 0, label: "Sun", short: "S" },
                      { day: 1, label: "Mon", short: "M" },
                      { day: 2, label: "Tue", short: "T" },
                      { day: 3, label: "Wed", short: "W" },
                      { day: 4, label: "Thu", short: "T" },
                      { day: 5, label: "Fri", short: "F" },
                      { day: 6, label: "Sat", short: "S" },
                    ].map(({ day, label, short }) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => {
                          if (selectedDays.includes(day)) {
                            setSelectedDays(
                              selectedDays.filter((d) => d !== day)
                            );
                          } else {
                            setSelectedDays([...selectedDays, day].sort());
                          }
                        }}
                        style={{
                          padding: "12px 8px",
                          borderRadius: "12px",
                          border: selectedDays.includes(day)
                            ? "2px solid var(--register-primary)"
                            : "1.5px solid var(--border-color)",
                          background: selectedDays.includes(day)
                            ? "var(--primary-light)"
                            : "var(--bg-input)",
                          color: selectedDays.includes(day)
                            ? "var(--register-primary)"
                            : "var(--text-primary)",
                          fontWeight: selectedDays.includes(day)
                            ? "700"
                            : "600",
                          fontSize: "13px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "4px",
                          minHeight: "56px",
                        }}
                        onMouseOver={(e) => {
                          if (!selectedDays.includes(day)) {
                            e.currentTarget.style.background =
                              "var(--bg-hover)";
                            e.currentTarget.style.borderColor =
                              "var(--primary)";
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!selectedDays.includes(day)) {
                            e.currentTarget.style.background =
                              "var(--bg-input)";
                            e.currentTarget.style.borderColor =
                              "var(--border-color)";
                          }
                        }}
                      >
                        <span style={{ fontSize: "11px", opacity: 0.7 }}>
                          {short}
                        </span>
                        <span style={{ fontSize: "14px" }}>{label}</span>
                      </button>
                    ))}
                  </div>
                  {selectedDays.length === 0 && (
                    <p
                      style={{
                        marginTop: "8px",
                        fontSize: "13px",
                        color: "var(--text-muted)",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "16px" }}
                      >
                        info
                      </span>
                      Please select at least one day
                    </p>
                  )}
                </div>
              )}

              {startDate && endDate && time && (
                <div className="form-section">
                  <div
                    style={{
                      padding: "12px 16px",
                      background: "var(--primary-light)",
                      borderRadius: "12px",
                      border: "1px solid var(--primary)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "20px", color: "var(--primary)" }}
                      >
                        info
                      </span>
                      <strong
                        style={{ color: "var(--primary)", fontSize: "14px" }}
                      >
                        Preview
                      </strong>
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        color: "var(--text-primary)",
                        lineHeight: "1.6",
                      }}
                    >
                      {(() => {
                        const dates = generateAppointmentDates(
                          new Date(startDate),
                          new Date(endDate),
                          frequency,
                          time,
                          selectedDays
                        );
                        const dayNames = [
                          "Sun",
                          "Mon",
                          "Tue",
                          "Wed",
                          "Thu",
                          "Fri",
                          "Sat",
                        ];
                        let frequencyText = "";
                        if (frequency === "daily") {
                          frequencyText = "every day";
                        } else if (frequency === "daily-monday-friday") {
                          frequencyText = "Monday to Friday";
                        } else if (frequency === "daily-monday-saturday") {
                          frequencyText = "Monday to Saturday";
                        } else if (frequency === "weekly") {
                          frequencyText = "every week";
                        } else if (
                          frequency === "selectedDays" &&
                          selectedDays.length > 0
                        ) {
                          const selectedDayLabels = selectedDays
                            .map((d) => dayNames[d])
                            .join(", ");
                          frequencyText = `on ${selectedDayLabels}`;
                        }
                        return `This will create ${dates.length} appointment${
                          dates.length !== 1 ? "s" : ""
                        } from ${new Date(
                          startDate
                        ).toLocaleDateString()} to ${new Date(
                          endDate
                        ).toLocaleDateString()} at ${time} ${
                          frequencyText ? `(${frequencyText})` : ""
                        }.`;
                      })()}
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="form-section">
                <label className="form-label">Date & Start Time</label>
                <div className="datetime-row">
                  <input
                    type="date"
                    className="form-input"
                    value={date}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      if (!isEditMode) {
                        const today = getTodayDate();
                        if (selectedDate < today) {
                          setError(
                            "Cannot select past dates. Please choose today or a future date."
                          );
                          return;
                        }
                      }
                      setError("");
                      setDate(selectedDate);
                    }}
                    min={isEditMode ? undefined : getTodayDate()}
                    required
                  />
                  <input
                    type="time"
                    className="form-input"
                    value={time}
                    onChange={(e) => {
                      setTime(e.target.value);
                      // Auto-set end time to 30 minutes after start (in 12-hour format)
                      if (e.target.value) {
                        const [hh, mm] = e.target.value
                          .split(":")
                          .map((v) => parseInt(v));
                        const startDate = new Date();
                        startDate.setHours(hh, mm, 0, 0);
                        const endDate = new Date(
                          startDate.getTime() + 30 * 60000
                        );
                        const endTimeStr = endDate.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        });
                        setEndTime(endTimeStr);
                      }
                      // Validate date and time together if both are set
                      if (date && e.target.value && !isEditMode) {
                        const [yy, mm, dd] = String(date)
                          .split("-")
                          .map((v) => parseInt(v, 10));
                        const [hh, min] = String(e.target.value)
                          .split(":")
                          .map((v) => parseInt(v, 10));
                        const datetime = new Date(
                          yy,
                          (mm || 1) - 1,
                          dd || 1,
                          hh || 0,
                          min || 0,
                          0,
                          0
                        );
                        const now = new Date();
                        if (datetime < now) {
                          setError(
                            "Cannot schedule appointments in the past. Please select a future date and time."
                          );
                        } else {
                          setError("");
                        }
                      } else {
                        setError("");
                      }
                    }}
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">End Time</label>
                <select
                  className="form-select"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  disabled={!time}
                >
                  <option value="">Select end time</option>
                  {generateEndTimeOptions(time).map((timeOption) => (
                    <option key={timeOption} value={timeOption}>
                      {timeOption}
                    </option>
                  ))}
                </select>
                {time && !endTime && (
                  <p
                    style={{
                      marginTop: "8px",
                      fontSize: "13px",
                      color: "var(--text-muted)",
                    }}
                  >
                    Please select an end time for the appointment
                  </p>
                )}
              </div>
            </>
          )}

          <div className="form-section">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              rows="4"
              placeholder="Add clinical notes or specific focus..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <button
            type="submit"
            className="primary-btn-full"
            disabled={
              saving || (patientsLoaded && patients.length === 0 && !isEditMode)
            }
          >
            {saving
              ? isEditMode
                ? "Updating..."
                : isMultiple
                ? "Creating Appointments..."
                : "Scheduling..."
              : isEditMode
              ? "Update Appointment"
              : isMultiple
              ? "Schedule Multiple Appointments"
              : "Schedule Appointment"}
          </button>
        </form>
      )}
    </div>
  );
}

export default AddAppointmentPage;
